import { Box, Card, CircularProgress, Typography } from '@mui/material'
import { OrganizationTaskProgress } from './OrganizationTaskProgress'
import { useEffect, useState } from 'react'
import { GetAnnualPlanTeam, GetDashboardTasks, GetUsersPlanRole } from '../../../services/AnnualPlanService'
import type { AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import type { PlanOrganizationTask } from '../../../models/PlanOrganizationTask'
import { AnnualPlanTeamCard } from './AnnualPlanTeamCard'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import { AnnualPlanRecentCommentsCard } from './AnnualPlanRecentCommentsCard'
import { RecentChangesCard } from '../implementation/RecentChangesCard'
import { useAuth } from '../../../hooks/use-auth'
import { OrganizationType } from '../../../utils/genericTypes'
import { toast } from 'react-toastify'
import { handleApiResponse } from '../../../utils/apiHelpers'

interface Props {
  rcnId: number
  annualPlanId: number
  changeTab: (tab: number) => void
  implementationPhase?: boolean
}

export function AnnualPlanDashboard (props: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(true)
  const [orgTasks, setOrgTasks] = useState<PlanOrganizationTask[]>([])
  const [planOrganizations, setPlanOrganizations] = useState<string[]>([])
  const [planTeam, setPlanTeam] = useState<AnnualPlanTeamMember[]>([])
  const [usersPlanRole, setUsersPlanRole] = useState<string>()
  const [isAuthorized, setIsAuthorized] = useState(true)
  const { user } = useAuth()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (props.annualPlanId == null) {
        return
      }

      setIsLoading(true)
      const { success, errors, response: team } = await GetAnnualPlanTeam(props.annualPlanId)
      if (!success) {
        for (const error of errors) toast.error(error)
        setIsLoading(false)
        return
      }
      setPlanTeam(team)

      const isMember = team.some(tm => tm.userId === user?.id)
      setIsAuthorized(isMember)

      if (isMember) {
        await Promise.all([
          fetchTaskProgress(),
          fetchUsersPlanRole()
        ])
      }

      setIsLoading(false)
    }

    void fetchData()
  }, [])

  const fetchTeam = async (): Promise<void> => {
    const { success, errors, response: team } = await GetAnnualPlanTeam(props.annualPlanId)
    if (!success) {
      for (const error of errors) toast.error(error)
      return
    }
    setPlanTeam(team)
  }

  const fetchTaskProgress = async (): Promise<void> => {
    const orgTasks: PlanOrganizationTask[] | null =
      await handleApiResponse(GetDashboardTasks(props.annualPlanId, props.implementationPhase ?? false))
    if (orgTasks == null) return
    const sortedTasks = orgTasks.sort(sortTasks)
    setOrgTasks(sortedTasks)
    const distinctOrgNames = sortedTasks.filter(
      (org, i, arr) => arr.findIndex(t => t.organizationName === org.organizationName) === i
    ).map(ot => { return ot.organizationName })
    setPlanOrganizations(distinctOrgNames)
  }

  const sortTasks = (a: PlanOrganizationTask, b: PlanOrganizationTask): number => {
    if (a.organizationType === OrganizationType.RCN && b.organizationType !== OrganizationType.RCN) {
      return -1
    } else if (b.organizationType === OrganizationType.RCN && a.organizationType !== OrganizationType.RCN) {
      return 1
    } else {
      return a.organizationName.localeCompare(b.organizationName)
    }
  }

  const fetchUsersPlanRole = async (): Promise<void> => {
    const role: string | null = await handleApiResponse(GetUsersPlanRole(props.annualPlanId))
    if (role != null) setUsersPlanRole(role)
  }

  if (isLoading) {
    return <CircularProgress></CircularProgress>
  }

  return <Box sx={{ display: 'flex' }}>
    {isAuthorized && <Card data-testid='planningprogress-card' sx={{ backgroundColor: 'var(--grey-100)', width: '60%', height: 'fit-content' }}>
      <Typography variant='h3' justifySelf='center' my='.5em'>{props.implementationPhase === true ? 'Implementation Progress' : 'Annual Planning Progress'}</Typography>

      {planOrganizations.map(po => {
        return <Box key={po} sx={{ m: '1em' }}>
          <OrganizationTaskProgress
            organizationName={po}
            organizationTasks={orgTasks.filter(task => task.organizationName === po)}
            changeTab={props.changeTab}
            implementationPhase={props.implementationPhase}
          />
        </Box>
      })}
    </Card>}

    <Box sx={{ width: '35%', ml: '1em', display: 'flex', flexDirection: 'column', gap: '1em' }}>
      <AnnualPlanTeamCard implementationPhase={props.implementationPhase} onTeamChange={fetchTeam} team={planTeam} annualPlanId={props.annualPlanId} readonly={usersPlanRole !== AnnualPlanRole.PlanAdministrator}></AnnualPlanTeamCard>

      {isAuthorized &&
        <AnnualPlanRecentCommentsCard annualPlanId={props.annualPlanId} rcnId={props.rcnId} changeTab={props.changeTab} />
      }

      {isAuthorized && props.implementationPhase === true &&
        <RecentChangesCard
          annualPlanId={props.annualPlanId}
          rcnId={props.rcnId}
          changeTab={props.changeTab}
          onResolved={() => { void fetchTaskProgress() }}
        />
      }
    </Box>
  </Box>
}
